import {Button, CalloutIcon} from '@wandb/components';
import React, {useCallback} from 'react';

import {useDispatch} from '../../state/hooks';
import {fixView as fixViewAction} from '../../state/workspaces/actions';
import {useTrackWorkspaceError} from './utils';
import {WorkspaceError} from './WorkspaceError';

type WorkspaceResetContentProps = {
  error: Error;
  workspaceStateId: string;
  viewType: 'project-view' | 'sweep-view' | 'group-view' | 'run-view';
  onResetError: () => void;
};

export const WorkspaceResetContent = ({
  error,
  workspaceStateId,
  viewType,
  onResetError,
}: WorkspaceResetContentProps) => {
  const dispatch = useDispatch();
  const fixView = useCallback(
    () => dispatch(fixViewAction(workspaceStateId)),
    [dispatch, workspaceStateId]
  );
  const trackWorkspaceError = useTrackWorkspaceError({
    action: 'Reset workspace',
    errorType: 'Error boundary',
    viewType,
  });

  return (
    <WorkspaceError
      title="Unable to render this workspace"
      header={
        <div className="mb-16">
          <CalloutIcon icon="warning" size="md" color="moon" />
        </div>
      }>
      <p className="text-center">
        An error occurred while trying to render your workspace.
      </p>
      <div className="mt-32 grid grid-cols-[auto_auto] gap-32 w-[600px] items-center">
        <div className="justify-self-end">
          <Button onPress={onResetError}>Reset error</Button>
        </div>
        <p>
          <strong>You can try resetting the error.</strong> This clears the
          error and makes no changes to your workspace, so it may fail again.
        </p>
        <div className="justify-self-end">
          <Button
            startIcon="reset"
            variant="destructive"
            data-dd-action-name="Reset workspace"
            onPress={() => {
              fixView();
              trackWorkspaceError(error);
            }}>
            Reset workspace
          </Button>
        </div>
        <p>
          <strong>You can reset this workspace.</strong> This will preserve
          runs, but custom charts and settings will be lost.
        </p>
      </div>
    </WorkspaceError>
  );
};
